
import { Table, TableShape, ArenaConfig, Zone, ZoneType, FloorDimensions, CategoryDefinition, WeekSchedule, Reservation } from './types';

// Diese Variable wird von Vite beim Build-Prozess durch den Wert von VITE_TENANT_ID ersetzt
declare const __TENANT_ID__: string;

const defaultDay = { open: "08:00", close: "23:00", isClosed: false };

const INITIAL_SCHEDULE: WeekSchedule = {
  Monday: { ...defaultDay },
  Tuesday: { ...defaultDay },
  Wednesday: { ...defaultDay },
  Thursday: { ...defaultDay },
  Friday: { ...defaultDay },
  Saturday: { open: "08:00", close: "22:00", isClosed: false },
  Sunday: { open: "09:00", close: "21:00", isClosed: false },
};

/**
 * Striktes Tenant-Management: Nur Build-Konstanten werden berücksichtigt.
 * URL-Parameter-Overriding wurde entfernt.
 */
const getTenantId = () => {
  if (typeof __TENANT_ID__ !== 'undefined' && __TENANT_ID__) {
      return __TENANT_ID__;
  }
  return 'demo';
};

export const ACTIVE_TENANT_ID = getTenantId();

export const INITIAL_CONFIG: ArenaConfig = {
  tenantId: ACTIVE_TENANT_ID,
  layoutTemplate: 'STANDARD',
  name: "SportArena Pro",
  address: "Sportallee 42, 12345 Hauptstadt",
  phoneLandline: "030 1234567",
  phoneMobile: "0170 9876543",
  contactEmail: "info@sportarena-pro.de",
  websiteUrl: "https://sportarena-pro.de",
  instagramUrl: "https://instagram.com",
  facebookUrl: "",
  twitterUrl: "",
  tiktokUrl: "",
  ownerName: "Alexander Sportowski",
  showOwnerOnStart: true,
  welcomeText: "Willkommen in der SportArena! Buche deine Spielfelder für Fußball, Tennis oder Badminton bequem online. Wir bieten modernste Böden und erstklassiges Equipment.",
  schedule: INITIAL_SCHEDULE,
  guestViewMode: 'SIMPLE',
  reservationMode: 'SPECIFIC_CATEGORY',
  lastReservationBuffer: 30,
  allowGuestReservations: true,
  requireReservationApproval: false,
  allowRecurringBookings: true, 
  recurringRequireApproval: true, 
  theme: {
    mode: 'MODERN',
    darkMode: false,
    primaryColor: '#3b82f6', 
    secondaryColor: '#1e293b', 
    calendarConfirmedColor: '#1e293b',
    calendarPendingColor: '#fbbf24' 
  },
  socialProviders: ['google', 'facebook'],
  packages: [
    {
      id: 'pkg-birthday',
      name: 'Kindergeburtstag Kickoff',
      icon: '🎂',
      categoryId: 'SOCCER_LARGE',
      allowedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      startTime: '10:00',
      endTime: '18:00',
      defaultPartySize: 12,
      durationMinutes: 120
    },
    {
      id: 'pkg-afterwork',
      name: 'After-Work Tennis & Drink',
      icon: '🎾',
      categoryId: 'TENNIS',
      allowedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      startTime: '17:00',
      endTime: '22:00',
      defaultPartySize: 4,
      durationMinutes: 90
    }
  ],
  seo: {
    pageTitle: '',
    metaDescription: '',
    metaKeywords: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    ogType: 'website',
    ogLocale: 'de_DE',
    twitterCard: 'summary_large_image',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    canonicalUrl: '',
    faviconUrl: '',
    themeColor: '#10b981',
    robotsDirective: 'index, follow',
    structuredData: '',
    customHeadTags: ''
  }
};

export const DEFAULT_CATEGORIES: CategoryDefinition[] = [
  { id: 'SOCCER_LARGE', name: 'Fußball (Groß)', color: '#ecfdf5', borderColor: '#10b981', icon: '⚽', minDuration: 90, defaultCapacity: 12 },
  { id: 'SOCCER_SMALL', name: 'Fußball (Klein)', color: '#f0fdf4', borderColor: '#22c55e', icon: '⚽', minDuration: 60, defaultCapacity: 6 },
  { id: 'TENNIS', name: 'Tennis', color: '#eff6ff', borderColor: '#3b82f6', icon: '🎾', minDuration: 60, defaultCapacity: 4 },
  { id: 'BADMINTON', name: 'Badminton', color: '#fff7ed', borderColor: '#3b82f6', icon: '🏸', minDuration: 45, defaultCapacity: 4 },
  { id: 'PADEL', name: 'Padel Tennis', color: '#f5f3ff', borderColor: '#8b5cf6', icon: '🎾', minDuration: 60, defaultCapacity: 4 },
  { id: 'GYM', name: 'Kursraum', color: '#fff1f2', borderColor: '#f43f5e', icon: '🏋️', minDuration: 60, defaultCapacity: 20 },
];

export const INITIAL_DIMENSIONS: FloorDimensions = {
  width: 1000,
  height: 800
};

export const INITIAL_ZONES: Zone[] = [
  { id: 'z1', name: 'Spielfeld-Halle 1', type: ZoneType.SPORT_AREA, x: 20, y: 20, width: 600, height: 500 },
  { id: 'z2', name: 'Umkleiden Damen', type: ZoneType.LOCKER_ROOM, x: 640, y: 20, width: 150, height: 200 },
  { id: 'z3', name: 'Umkleiden Herren', type: ZoneType.LOCKER_ROOM, x: 810, y: 20, width: 150, height: 200 },
  { id: 'z4', name: 'Sport-Lounge & Bar', type: ZoneType.LOUNGE, x: 640, y: 240, width: 320, height: 280 },
  { id: 'z5', name: 'Haupteingang', type: ZoneType.ENTRANCE, x: 20, y: 540, width: 200, height: 100 },
];

export const INITIAL_TABLES: Table[] = [
  { id: 't1', name: 'Court A (Fußball)', capacity: 12, shape: TableShape.RECTANGLE, category: 'SOCCER_LARGE', x: 50, y: 50, width: 250, height: 150, rotation: 0 },
  { id: 't2', name: 'Court B (Fußball)', capacity: 12, shape: TableShape.RECTANGLE, category: 'SOCCER_LARGE', x: 50, y: 220, width: 250, height: 150, rotation: 0 },
  { id: 't3', name: 'Tennis 1', capacity: 4, shape: TableShape.RECTANGLE, category: 'TENNIS', x: 350, y: 50, width: 180, height: 100, rotation: 0 },
  { id: 't4', name: 'Tennis 2', capacity: 4, shape: TableShape.RECTANGLE, category: 'TENNIS', x: 350, y: 170, width: 180, height: 100, rotation: 0 },
  { id: 't5', name: 'Badminton 1', capacity: 4, shape: TableShape.RECTANGLE, category: 'BADMINTON', x: 350, y: 300, width: 80, height: 120, rotation: 0 },
  { id: 't6', name: 'Badminton 2', capacity: 4, shape: TableShape.RECTANGLE, category: 'BADMINTON', x: 450, y: 300, width: 80, height: 120, rotation: 0 },
];

export const INITIAL_RESERVATIONS: Reservation[] = [];
